/**
 * VisualizationNode Component - MA-ARCH-164
 * 
 * Renders a single node in the funnel visualization.
 * Extracted from FunnelVisualization.jsx.
 */

import React, { useRef, useLayoutEffect } from 'react';
import { cn } from '@/lib/utils';
import { GitBranch, Pencil, ChevronRight } from 'lucide-react';
import {
  NODE_WIDTH,
  NODE_HEIGHT,
  NODE_WIDTH_HOVERED,
  NODE_HEIGHT_HOVERED_MIN,
} from '../utils/layoutConstants';
import {
  STEP_TYPE_COLORS,
  getNodeDisplayName,
  isInternalLabel,
  getCleanLabel,
} from '../utils/nodeConfig';

/**
 * VisualizationNode - Renders a funnel step node with hover expansion.
 * 
 * @param {Object} props
 * @param {Object} props.node - The node data
 * @param {boolean} props.isSelected - Whether node is selected
 * @param {boolean} props.isEntry - Whether node is entry point
 * @param {boolean} props.isSubmitStep - Whether node is submit step
 * @param {boolean} props.isTerminal - Whether node is terminal
 * @param {boolean} props.isBranching - Whether node has multiple outgoing edges
 * @param {boolean} props.isHovered - Whether node is being hovered
 * @param {boolean} props.isAnyNodeHovered - Whether any node is being hovered
 * @param {Function} props.onClick - Click handler
 * @param {Function} props.onHover - Hover handler
 * @param {Function} props.onCardHeightChange - Height measurement callback
 * @param {Array} props.responses - Response options for the node
 * @param {string} props.message - Node message content
 * @param {number} props.clearanceOffset - Vertical offset for clearance
 */
export function VisualizationNode({
  node,
  isSelected,
  isEntry,
  isSubmitStep,
  isTerminal,
  isBranching,
  isHovered,
  isAnyNodeHovered,
  onClick,
  onHover,
  onCardHeightChange,
  responses = [],
  message = '',
  clearanceOffset = 0,
}) {
  const config = STEP_TYPE_COLORS[node.node_type] || STEP_TYPE_COLORS.response;
  const Icon = config.icon;
  
  const displayName = getNodeDisplayName(node);
  
  const cleanResponses = responses.filter(r => !isInternalLabel(r.label));
  const hasCleanResponses = cleanResponses.length > 0;
  
  const isDimmed = isAnyNodeHovered && !isHovered && !isSelected;
  
  // Ref to measure actual card height
  const cardRef = useRef(null);
  
  // Measure and report actual height for ALL nodes
  useLayoutEffect(() => {
    if (!cardRef.current || !onCardHeightChange) {
      return;
    }
    
    const measureAndReport = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        const actualHeight = rect.height;
        onCardHeightChange(node.id, actualHeight, isHovered);
      }
    };
    
    // Use ResizeObserver to catch size changes (including hover expansion)
    const resizeObserver = new ResizeObserver(() => {
      measureAndReport();
    });
    
    resizeObserver.observe(cardRef.current);
    
    // Measure immediately on mount
    measureAndReport();
    
    // Also measure after CSS transitions complete (200ms transition)
    const timeoutId = setTimeout(measureAndReport, 250);
    
    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [node.id, onCardHeightChange, isHovered, message, cleanResponses.length]);
  
  return (
    <div
      className={cn(
        'group relative cursor-pointer select-none',
        'transition-all duration-200 ease-out'
      )}
      onClick={() => onClick?.(node.id)}
      onMouseEnter={() => onHover?.(node.id)}
      onMouseLeave={() => onHover?.(null)}
      data-testid={`viz-node-${node.id}`}
      style={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        transform: clearanceOffset > 0 ? `translateY(${clearanceOffset}px)` : undefined,
      }}
    >
      {/* Card with OPAQUE hover background, centered expansion */}
      <div
        ref={cardRef}
        className={cn(
          'absolute rounded-lg border',
          'transition-all duration-200 ease-out',
          !isHovered && config.bgColor,
          !isHovered && config.borderColor,
          isHovered && 'bg-slate-800',
          isHovered && config.borderColorHover,
          isHovered && 'shadow-2xl shadow-black/50',
          isHovered && 'z-40',
          isSelected && 'ring-2 ring-indigo-400 ring-offset-1 ring-offset-slate-900 shadow-lg shadow-indigo-500/20',
          isEntry && !isSelected && !isHovered && 'shadow-sm shadow-emerald-500/15',
          isBranching && 'border-[1.5px]',
          isDimmed && 'opacity-60'
        )}
        style={{
          width: isHovered ? NODE_WIDTH_HOVERED : NODE_WIDTH,
          left: isHovered ? -(NODE_WIDTH_HOVERED - NODE_WIDTH) / 2 : 0,
          top: isHovered ? -8 : 0,
          minHeight: isHovered ? NODE_HEIGHT_HOVERED_MIN : NODE_HEIGHT,
        }}
      >
        {/* Top accent bar */}
        <div className={cn(
          'absolute top-0 left-3 right-3 h-[2px] rounded-full',
          config.accentColor
        )} />
        
        {/* ENTRY badge */}
        {isEntry && (
          <div className={cn(
            'absolute -top-3 -left-2 z-30',
            'flex items-center gap-1 px-2 py-0.5 rounded-full',
            'bg-emerald-600/80 backdrop-blur-sm',
            'border border-emerald-400/60',
            'text-emerald-50 text-[9px] font-bold uppercase tracking-wide',
            'shadow-md shadow-emerald-900/30'
          )}>
            Entry
          </div>
        )}
        
        {/* SUBMIT badge - positioned to visually match ENTRY badge overhang */}
        {isSubmitStep && !isEntry && (
          <div className={cn(
            'absolute -top-3 -left-1 z-30',
            'flex items-center gap-1 px-2 py-0.5 rounded-full',
            'bg-blue-600/80 backdrop-blur-sm',
            'border border-blue-400/60',
            'text-blue-50 text-[9px] font-bold uppercase tracking-wide',
            'shadow-md shadow-blue-900/30'
          )}>
            Submit
          </div>
        )}
        
        {/* When both ENTRY and SUBMIT - show side by side */}
        {isSubmitStep && isEntry && (
          <div className={cn(
            'absolute -top-3 left-10 z-30',
            'flex items-center gap-1 px-2 py-0.5 rounded-full',
            'bg-blue-600/80 backdrop-blur-sm',
            'border border-blue-400/60',
            'text-blue-50 text-[9px] font-bold uppercase tracking-wide',
            'shadow-md shadow-blue-900/30'
          )}>
            Submit
          </div>
        )}
        
        {/* END badge - MA-FUNNEL-OUTCOME-STD-002: show outcome with color coding */}
        {isTerminal && !isEntry && !isSubmitStep && (
          <div className={cn(
            'absolute -top-3 -left-2 z-30',
            'flex items-center gap-1 px-2 py-0.5 rounded-full',
            'backdrop-blur-sm',
            'text-[9px] font-bold uppercase tracking-wide',
            'shadow-md',
            node.content?.outcome === "qualified" ? "bg-green-600/80 border border-green-400/60 text-green-50 shadow-green-900/30" :
            node.content?.outcome === "not_qualified" ? "bg-red-600/80 border border-red-400/60 text-red-50 shadow-red-900/30" :
            node.content?.outcome === "abandoned" ? "bg-amber-600/80 border border-amber-400/60 text-amber-50 shadow-amber-900/30" :
            node.content?.outcome === "no_consent" ? "bg-orange-600/80 border border-orange-400/60 text-orange-50 shadow-orange-900/30" :
            node.content?.outcome === "error" ? "bg-slate-600/80 border border-slate-400/60 text-slate-50 shadow-slate-900/30" :
            "bg-red-600/80 border border-red-400/60 text-red-50 shadow-red-900/30"
          )} data-testid={`viz-end-badge-${node.id}`}>
            End{node.content?.outcome ? ` · ${
              node.content.outcome === "not_qualified" ? "Not Qual" :
              node.content.outcome === "no_consent" ? "No Consent" :
              node.content.outcome.charAt(0).toUpperCase() + node.content.outcome.slice(1)
            }` : ""}
          </div>
        )}
        
        {/* Edit badge */}
        <div className={cn(
          'absolute -top-3 -right-2 z-30',
          'opacity-0 group-hover:opacity-100 transition-opacity duration-150'
        )}>
          <div className={cn(
            'flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer',
            'bg-slate-800/90 backdrop-blur-sm',
            'border border-slate-600/50',
            'text-slate-200 text-[10px] font-medium',
            'hover:bg-slate-700',
            'shadow-md',
            'transition-all duration-150'
          )}>
            <Pencil className="h-2.5 w-2.5" />
            <span>Edit</span>
          </div>
        </div>
        
        {/* Content area */}
        <div className={cn(
          'px-3 py-2 pt-3',
          isHovered && 'px-4 py-3 pt-4'
        )}>
          {/* Header row */}
          <div className="flex items-center gap-2">
            <div className={cn(
              'flex-shrink-0 rounded flex items-center justify-center',
              isHovered ? 'w-6 h-6' : 'w-5 h-5',
              'bg-slate-700/60'
            )}>
              <Icon className={cn(
                isHovered ? 'h-3.5 w-3.5' : 'h-3 w-3',
                config.iconColor
              )} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className={cn(
                'font-semibold truncate leading-tight',
                isHovered ? 'text-[13px] text-white' : 'text-[11px] text-slate-100'
              )}>
                {displayName}
              </h4>
              <span className={cn(
                'uppercase tracking-wider font-medium',
                isHovered ? 'text-[9px]' : 'text-[8px]',
                config.textColor
              )}>
                {config.label}
              </span>
            </div>
            
            {isBranching && !isHovered && (
              <GitBranch className="h-3 w-3 text-slate-500 flex-shrink-0" />
            )}
          </div>
          
          {/* Hover preview content */}
          {isHovered && (
            <div className="mt-3 animate-in fade-in duration-150">
              {message && (
                <p className="text-[11px] text-slate-200 leading-relaxed mb-3">
                  {message}
                </p>
              )}
              
              {hasCleanResponses && (
                <div className="pt-2 border-t border-slate-600/50">
                  <div className="text-[8px] text-slate-400 uppercase tracking-wider mb-1.5">
                    Response Options
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cleanResponses.slice(0, 5).map((resp, idx) => (
                      <span 
                        key={resp.id || idx} 
                        className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-md bg-slate-700/80 text-slate-100 border border-slate-600/50"
                      >
                        <ChevronRight className="h-2.5 w-2.5 text-slate-400 flex-shrink-0" />
                        <span className="truncate max-w-[100px]">
                          {getCleanLabel(resp.label, `Option ${idx + 1}`)}
                        </span>
                      </span>
                    ))}
                    {cleanResponses.length > 5 && (
                      <span className="text-[10px] px-2 py-1 text-slate-400">
                        +{cleanResponses.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VisualizationNode;
